import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useSettings } from '@/lib/settings';
import SEO from '@/components/SEO';
import Breadcrumb from '@/components/Breadcrumb';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Helmet } from 'react-helmet-async';
import { MapPin, Calendar, Wrench, ArrowRight, Phone, ArrowLeft } from 'lucide-react';

// Hardcoded defaults used ONLY if the "Reference" page record or its page_header section
// is missing in the CMS. Editable values live in /admin/pages → Reference → Sekcije → Header stranice.
const FALLBACK = {
    eyebrow: 'Projekti',
    heading: 'Naše reference',
    subheading: 'Pogled u dio onoga što smo realizirali u Poreču, Rovinju, Vrsaru i drugim mjestima Istre.',
    background_image: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?auto=format&fit=crop&w=2000&q=80',
    image_alt: 'Škobić Keramika reference',
};

export default function ReferenceList() {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pageMeta, setPageMeta] = useState(null);
    const { settings } = useSettings();

    useEffect(() => {
        api.get('/public/projects').then(({ data }) => setProjects(data)).catch(() => {}).finally(() => setLoading(false));
        // Load the "Reference" page record so the hero is editable from CMS (Header stranice section)
        api.get('/public/page/reference').then(({ data }) => setPageMeta(data)).catch(() => {});
    }, []);

    // Pull values from page_header section (CMS) with fallback to defaults
    const header = (pageMeta?.sections || []).find((s) => s.type === 'page_header' && s.enabled !== false);
    const hd = header?.data || {};
    const eyebrow = hd.eyebrow || FALLBACK.eyebrow;
    const heading = hd.heading || pageMeta?.h1 || FALLBACK.heading;
    const subheading = hd.subheading || FALLBACK.subheading;
    const bgImage = hd.background_image || FALLBACK.background_image;
    const imgAlt = hd.image_alt || FALLBACK.image_alt;
    const seo = pageMeta?.seo || {};

    return (
        <>
            <SEO
                title={seo.meta_title || pageMeta?.title || 'Reference – Škobić Keramika Poreč'}
                description={seo.meta_description || pageMeta?.description || 'Pregled odabranih keramičarskih projekata u Poreču i Istri.'}
                image={seo.og_image || pageMeta?.featured_image}
                noindex={seo.robots_index === false}
                nofollow={seo.robots_follow === false}
            />
            <section className="relative overflow-hidden border-b border-border">
                <img
                    src={bgImage}
                    alt={imgAlt}
                    className="absolute inset-0 h-full w-full object-cover"
                    fetchpriority="high"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/50 to-black/65" />
                <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-28 pb-16 sm:pt-32 sm:pb-20 lg:pt-36 lg:pb-24">
                    {eyebrow && <p className="text-xs uppercase tracking-[0.18em] text-white/85 mb-4">{eyebrow}</p>}
                    <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight">{heading}</h1>
                    {subheading && (
                        <p className="mt-4 text-white/85 text-lg max-w-2xl">
                            {subheading}
                        </p>
                    )}
                </div>
            </section>

            <div className="sk-container pt-6">
                <Breadcrumb items={[{ name: 'Reference', url: '/reference' }]} />
            </div>

            <section className="sk-section">
                <div className="sk-container">
                    {loading ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {[1,2,3,4,5,6].map((i) => <Skeleton key={i} className="aspect-[4/3] rounded-2xl" />)}
                        </div>
                    ) : projects.length === 0 ? (
                        <p className="text-center text-foreground/70">Trenutno nema referenci.</p>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {projects.map((p) => (
                                <Link
                                    key={p.id}
                                    to={`/reference/${p.slug}`}
                                    className="group relative overflow-hidden rounded-2xl border border-border bg-white"
                                    data-testid="reference-card"
                                >
                                    <div className="aspect-[4/3] overflow-hidden">
                                        <img src={p.featured_image} alt={p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]" loading="lazy" />
                                    </div>
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/0" />
                                    <div className="absolute inset-x-0 bottom-0 p-5 text-white">
                                        <p className="text-[10px] uppercase tracking-[0.18em] text-white/85">{p.location} · {p.year}</p>
                                        <h2 className="mt-1 font-display text-xl sm:text-2xl tracking-tight">{p.title}</h2>
                                        <p className="mt-1 text-xs text-white/80">{p.type_of_work}</p>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </>
    );
}
